<template>
  <div>
    <v-dialog v-model="model" max-width="800" persistent scrollable>
      <material-card
      :title="$t('views.plandownloadcenter.add_file')"
        :color="$vuetify.theme.themes.light.success"
        icon="add"
        v-if="proofInfo"
      >
        <v-card-text>
          <v-form v-model="valid">
            <v-row row class="flex-nowrap" align="center">
              <v-col xs12 md4>
                <trans-enum
                  :value.sync="proofInfo.ProofType"
                  :items="proofTypes"
                  :label="$t('headers.inmemoryfiles.type_of_file')"
                  :rules="[$rules.required]"
                ></trans-enum>
              </v-col>
            </v-row>
            <template v-if="proofInfo.ProofType">
              <v-row row no-gutters class="flex-nowrap" align="center">
                <v-col xs12 sm12 md8 lg6>
                  <multi-file-upload2
                    id="addFileId"
                    :filesIds.sync="filesIds"
                    :files="externalFiles"
                    @emit-upload-start="saving = true"
                    @emit-upload-end="saving = false"
                    :label="
                      $t('components.claims.add_file_dialog.select_file(s)')
                    "
                    :tags="[planNumber]"
                    :categories="[proofInfo.ProofType]"
                    :currentFolder="planName"
                    :zipDownloadUrl="
                      settings.DOCUMENTSERVICEURL + 'api/ZipFilesDownload'
                    "
                    :fileUploadUrl="
                      settings.DOCUMENTSERVICEURL + 'api/UploadFiles'
                    "
                    @emit-files="
                      bindFile($event);
                      saving = false;
                    "
                    @emit-upload-error="
                      error = $event;
                      saving = false;
                    "
                    :rules="[$rules.atLeastOne]"
                    class="required"
                    :getFilesUrl="settings.DOCUMENTSERVICEURL + 'api/Files'"
                  />
                </v-col>
              </v-row>
              <v-row row class="flex-nowrap" align="center">
                <v-col xs12 md4 v-if="proofInfo.ProofType === 'Referral'">
                  <pbas-date-picker
                    :label="
                      $t('components.claims.add_file_dialog.referral_date')
                    "
                    :value="proofInfo.ReferralDate"
                    @update-date="proofInfo.ReferralDate = $event"
                    :rules="[$rules.required, rules.referralDate]"
                    class="required"
                    :allowedDates="referralDates"
                  ></pbas-date-picker>
                </v-col>
                <v-col xs12 md4>
                  <no-autocomplete-textfield
                    v-model="proofInfo.Name"
                    :label="$t('globals.name')"
                  />
                </v-col>
                <v-col xs12 md4>
                  <v-select
                    :value="proofInfo.ClaimLines"
                    :items="claimLines"
                    :label="$t('headers.proof.claim_lines')"
                    multiple
                    :rules="[$rules.atLeastOne]"
                    class="required"
                    @change="change"
                  >
                    <v-list-item slot="prepend-item" ripple @click="toggle">
                      <v-list-item-action>
                        <v-icon>
                          {{ icon() }}
                        </v-icon>
                      </v-list-item-action>
                      <v-list-item-content>
                        <v-list-item-title>
                          {{ $t("components.trans-multi-select2.select_all") }}
                        </v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                    <v-divider slot="prepend-item" class="mt-2" />
                    <template v-slot:selection="data">
                      {{ data.item }}
                    </template>
                    <template v-slot:item="data">
                      <v-list-item v-bind="data.attrs" v-on="data.on">
                        <v-list-item-action>
                          <v-checkbox
                            v-model="data.attrs.inputValue"
                          ></v-checkbox>
                        </v-list-item-action>
                        <v-list-item-content>
                          <v-list-item-title>
                            {{ data.item }}
                          </v-list-item-title>
                        </v-list-item-content>
                      </v-list-item>
                    </template>
                  </v-select>
                </v-col>
              </v-row>
            </template>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-btn
            text
            @click="
              proofInfo = null;
              $emit('close');
            "
          >
            {{ $t("globals.close") }}
          </v-btn>
          <v-btn
            color="primary"
            :loading="saving"
            @click="save"
            :disabled="!valid"
          >
            {{ $t("globals.save") }}
          </v-btn>
        </v-card-actions>
      </material-card>
    </v-dialog>
    <error-handler :error="error"></error-handler>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import ErrorHandler from "@/components/ErrorHandler";
import { locker } from "@/mixins/locker";
import { createProofFile } from "@/makeMemberFile";
import MultiFileUpload2 from "@/components/MultiFileUpload2.vue";
import TransEnum from "../TransEnum.vue";
import NoAutocompleteTextfield from "../NoAutocompleteTextfield.vue";

export default {
  components: {
    ErrorHandler,
    MultiFileUpload2,
    TransEnum,
    NoAutocompleteTextfield,
  },
  mixins: [locker],
  props: ["model", "claimLines", "planNumber", "planName", "proof", "externalFiles", "isSickClaim"],
  data() {
    return {
      error: null,
      saving: false,
      valid: false,
      proofInfo: null,
      files: null,
      filesIds: [],
      rules: {
        referralDate: (v) => {
          if (v === undefined || v === null || v === "") {
            return true;
          }
          const now = new Date();
          let yearsAgo5 = new Date();
          yearsAgo5.setFullYear(yearsAgo5.getFullYear() - 5);
          if (!this.isDate(v)) {
            return this.$t("validation.not_a_date");
          }
          const n = Date.parse(v);
          const d = new Date(n);
          if(d <= yearsAgo5) {
            return this.$t("validation.date_greater_{0}", [this.formatDate(yearsAgo5)]);
          }
          if(d > now) {
            return this.$t("validation.date_lower_{0}", [this.formatDate(now)]);
          }
        },
      },
    };
  },
  computed: {
    ...mapGetters("plansMembers", ["options"]),
    ...mapGetters("common", ["settings"]),
    proofTypes() {
      if (this.isSickClaim) {
        return this.options.Enums.ProofType.filter(o=> o==="SickDayForm" || o==="DoctorNote");
      }
      return this.options.Enums.ProofType.filter(o=> o!=="SickDayForm" && o!=="DoctorNote" && o!=="EmployerConfirmation");
    }
  },
  watch: {
    proof: {
      handler(v) {
        if (v) {
          this.proofInfo = JSON.parse(JSON.stringify(v));
          if (this.proofInfo.FileSegments) {
            for (const fileSegment of this.proofInfo.FileSegments) {
              this.filesIds.push(fileSegment.PhysicalFileId);
            }
          }
          this.files = this.proofInfo;
        }
      },
    },
    model: {
      handler(v) {
        if (!v) {
          this.filesIds = [];
        }
      }
    }
  },
  mounted() {
    if (this.proof) {
      this.proofInfo = JSON.parse(JSON.stringify(this.proof));
      if (this.proofInfo.FileSegments) {
        for (const fileSegment of this.proofInfo.FileSegments) {
          this.filesIds.push(fileSegment.PhysicalFileId);
        }
      }
      this.files = this.proofInfo;
    }
  },
  methods: {
    formatDate(d) {
      let month = (d.getUTCMonth() + 1).toString();
      if (month.length === 1) {
        month = "0" + month;
      }
      let day = d.getUTCDate().toString();
      if (day.length === 1) {
        day = "0" + day;
      }
      return `${d.getUTCFullYear()}-${month}-${day}`;
    },
    isDate(val) {
      if (
        new RegExp(/([12]\d{3}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01]))/).test(
          val
        )
      ) {
        return !isNaN(Date.parse(val));
      }
      return false;
    },
    referralDates(val) {
      const n = Date.parse(val);
      const d = new Date(n);
      // const now = new Date();
      let yearsAgo5 = new Date();
      yearsAgo5.setFullYear(yearsAgo5.getFullYear() - 5);
      const now = new Date();
      return now >= d  && d > yearsAgo5;
    },
    bindFile(memberFiles) {
      // console.log("bind files: ", memberFiles);
      if (memberFiles.length > 0) {
        this.files = createProofFile(memberFiles, this.proofInfo.ProofType);
      } else {
        this.files = null;
      }
    },
    save() {
      // console.log(this.files);
      this.proofInfo.FileSegments = this.files.FileSegments;
      this.$emit("save", this.proofInfo);
    },
    icon() {
      if (this.allItems()) return "mdi-close-box";
      if (this.someItems()) return "mdi-minus-box";
      return "mdi-checkbox-blank-outline";
    },
    allItems() {
      return (
        this.proofInfo.ClaimLines &&
        this.proofInfo.ClaimLines.length === this.claimLines.length
      );
    },
    someItems() {
      return (
        this.proofInfo.ClaimLines &&
        this.proofInfo.ClaimLines.length > 0 &&
        !this.allItems()
      );
    },
    toggle() {
      this.$nextTick(() => {
        if (this.allItems()) {
          this.change([]);
        } else {
          this.change(this.claimLines.slice());
        }
      });
    },
    change($event) {
      let items = $event.filter((o) => o !== null && o !== undefined);
      this.proofInfo.ClaimLines = items;
      this.$forceUpdate();
    },
  },
};
</script>
